import { useCallback, useEffect, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export type StaffRole = "admin" | "sales" | "matchmaker";

export interface AuthRoleState {
  session: Session | null;
  role: StaffRole | null;
  username: string | null;
  loading: boolean;
  isAdmin: boolean;
  isMatchmaker: boolean;
  reload: () => Promise<void>;
}

export function useAuthRole(): AuthRoleState {
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<StaffRole | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const sessionRef = useRef<Session | null>(null);

  const fetchRole = useCallback(async (current: Session | null) => {
    if (!current) {
      setRole(null);
      setUsername(null);
      return;
    }
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role, username")
        .eq("user_id", current.user.id)
        .maybeSingle();
      if (error) throw error;
      setRole((data?.role as StaffRole) || null);
      setUsername((data?.username as string) || null);
    } catch {
      setRole(null);
      setUsername(null);
    }
  }, []);

  const reload = useCallback(async () => {
    await fetchRole(sessionRef.current);
  }, [fetchRole]);

  useEffect(() => {
    let active = true;

    supabase.auth
      .getSession()
      .then(async ({ data }) => {
        const current = data.session ?? null;
        if (!active) return;
        sessionRef.current = current;
        setSession(current);
        await fetchRole(current);
        if (active) setLoading(false);
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      sessionRef.current = nextSession;
      setSession(nextSession);
      void fetchRole(nextSession);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [fetchRole]);

  return {
    session,
    role,
    username,
    loading,
    isAdmin: role === "admin",
    isMatchmaker: role === "matchmaker",
    reload,
  };
}

export default useAuthRole;